import type { Contact } from '../../../domain/contact';
import type { ContactsToExportPayload, Crm, LeadsToExportPayload } from '../../../domain/crm';
import { CrmDisplayName } from '../../../domain/crm';
import type { AppStore } from '../../../services/appStore';
import { translate } from '../../../services/i18n';
import type { ContactRepository } from '../../contacts/repository';
import type { QuotaRepository } from '../../quota/repository';
import { crmApi } from '../api';

import type { ExportContactsToCrmRepository, PartialSuccessToast } from './useCase';

export const makeExportContactsToCrmRepository = (
  appStore: AppStore,
  contactRepository: ContactRepository,
  quotaRepository: QuotaRepository
): ExportContactsToCrmRepository => ({
  exportAsContacts: (payload: ContactsToExportPayload, crm: Crm) => crmApi.exportAsContacts(payload, crm),
  exportAsLeads: (payload: LeadsToExportPayload) => crmApi.exportAsLeads(payload),
  showLoadingToast: (count: number, crm: Crm) => {
    appStore.setToast({
      content: translate('contacts.bulk.export_to_crm.toast.loading', { count, crm: CrmDisplayName[crm] }),
      isLoading: true,
      showCloseIcon: false,
    });
  },
  showSuccessToast: () => {
    appStore.setSuccessToast({
      content: translate('contacts.bulk.export_to_crm.toast.success'),
      showCloseIcon: true,
    });
  },
  showPartialSuccessToast: ({
    totalCount,
    successCount,
    link,
    revealedSuccessCount,
    revealedTotalCount,
  }: PartialSuccessToast) => {
    appStore.setSuccessToast({
      content: revealedTotalCount
        ? translate('contacts.bulk.export_to_crm.toast.success_reveal_partial', {
            totalCount,
            successCount,
            revealedSuccessCount,
            revealedTotalCount,
          })
        : translate('contacts.bulk.export_to_crm.toast.success_partial', {
            totalCount,
            successCount,
          }),
      showCloseIcon: true,
      linkText: translate('contacts.bulk.export_to_crm.toast.log'),
      link,
    });
  },
  showErrorToast: (link?: string) => {
    appStore.setErrorToast({
      content: translate('contacts.bulk.export_to_crm.toast.failed'),
      showCloseIcon: true,
      linkText: translate('contacts.bulk.export_to_crm.toast.log'),
      link,
    });
  },
  updateContact: (
    contactId: string,
    linkObject: Pick<Contact, 'hubspotContactUrl' | 'salesforceLeadUrl' | 'salesforceContactUrl'>
  ) => {
    const contact = contactRepository.getContactById(contactId);
    if (!contact) {
      console.error(`Contact with id ${contactId} not found`);
      return;
    }

    contactRepository.saveContact({ ...contact, ...linkObject });
  },
  decreaseExportsQuota: quotaRepository.decreaseContactsQuota,
});
